import { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTheme } from '@/contexts/ThemeContext';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Activity, TrendingUp, Target, Brain, Upload,
  CreditCard, User, LogOut, Users, FileText, Grid3X3,
  CalendarClock, Bell, GitCompare, Palette, Plug, ChevronDown, MessageCircle
, Shield, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SupportChat from '@/components/SupportChat';
import DateRangePicker from '@/components/DateRangePicker';
import { useDateRange } from '@/contexts/DateRangeContext';

export default function DashboardLayout({ children, studioSections, activeStudioSection, onStudioSectionChange, hideDatePicker = false }) {
  const { user, logout } = useContext(AuthContext);
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspace();
  const [wsOpen, setWsOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [studioOpen, setStudioOpen] = useState(false);
  const { range, setRange } = useDateRange();

  const isStudio = location.pathname === '/marketing-studio' || location.pathname === '/studio';
  const esAdmin = window.__adpulse_user_email === 'sebastian.mellado.cl@gmail.com' || window.__adpulse_user_email === 'sebastian.mellado.a@gmail.com';

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { path: '/custom-dashboard', label: 'Dashboard Pro', icon: <Grid3X3 className="w-5 h-5" /> },
    ...(esAdmin ? [{ path: '/health-score', label: 'Ad Health Score', icon: <Activity className="w-5 h-5" /> }] : []),
    { path: '/analytics', label: 'Analytics', icon: <TrendingUp className="w-5 h-5" /> },
    { path: '/seo-copilot', label: 'SEO Copilot', icon: <Brain className="w-5 h-5" /> },
    { path: '/whatsapp-roi', label: 'WhatsApp ROI', icon: <MessageCircle className="w-5 h-5" /> },
    
    { path: '/organic', label: 'Orgánico', icon: <Users className="w-5 h-5" /> },
    { path: '/comparison', label: 'Comparación', icon: <GitCompare className="w-5 h-5" /> },
    { path: '/matrix', label: 'Matriz' },
    { path: '/campaigns', label: 'Campañas', icon: <Target className="w-5 h-5" /> },
    { path: '/google-ads-analysis', label: 'Google Ads IA', icon: <Bot className="w-5 h-5" /> },
    { path: '/v2/brands', label: 'Clientes', icon: <Users className="w-5 h-5" /> },

    { path: '/scheduled-reports', label: 'Reportes Auto', icon: <CalendarClock className="w-5 h-5" /> },
    { path: '/alerts', label: 'Alertas', icon: <Bell className="w-5 h-5" /> },
    { path: '/ai-insights', label: 'AI Insights', icon: <Brain className="w-5 h-5" /> },
    { path: '/branding', label: 'Marca Blanca', icon: <Palette className="w-5 h-5" /> },
    ...(esAdmin ? [{ path: '/subscription', label: 'Suscripción', icon: <CreditCard className="w-5 h-5" /> }] : []),
    { path: '/profile', label: 'Perfil', icon: <User className="w-5 h-5" /> },
    ...(window.__adpulse_user_email === 'sebastian.mellado.cl@gmail.com' || window.__adpulse_user_email === 'sebastian.mellado.a@gmail.com' ? [{ path: '/panel-admin', label: 'Admin', icon: <Shield className="w-5 h-5" /> }] : [])
  ];

  // El jefe (no admin) solo ve 4 pestañas
  const PESTANAS_JEFE = ['/dashboard', '/custom-dashboard', '/analytics', '/comparison', '/v2/brands', '/matrix'];
  const _vistaCliente = new URLSearchParams(window.location.search).get('vista') === 'cliente';
  const navItemsFinal = (esAdmin && !_vistaCliente) ? navItems : navItems.filter(i => PESTANAS_JEFE.includes(i.path));

  const handleLogout = () => { logout(); navigate('/login'); };


  const sections = studioSections || [
    { id: 'cover',   name: 'Portada',          widgets: [] },
    { id: 'summary', name: 'Resumen General',  widgets: [] },
    { id: 'meta',    name: 'Meta Ads',         widgets: [] },
    { id: 'google',  name: 'Google Ads',       widgets: [] },
    { id: 'ga4',     name: 'Google Analytics', widgets: [] },
    { id: 'gsc',     name: 'Search Console',   widgets: [] },
  ];

  if (location.pathname === '/studio') return <>{children}</>;

  return (
    <div className={`min-h-screen ${theme.bg} flex transition-colors duration-300`}>
      {/* Sidebar - identico al original */}
      <motion.aside
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className={`w-72 ${theme.sidebar} border-r flex flex-col p-6`}
      >
        {/* Logo */}
        <div className="flex items-center space-x-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ai-glow to-brand-meta" />
          <span className={`font-heading font-bold text-xl ${theme.text}`}>AdPulse <span className="text-blue-600">IA</span></span>
        </div>

        {/* Active brand indicator (read-only). Para cambiar de marca: /v2/brands */}
        {activeWorkspace && (
          <div style={{marginBottom:16,padding:'10px 12px',borderRadius:10,border:'1px solid #e2e8f0',background:'white',display:'flex',alignItems:'center',gap:10,boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
            <div style={{width:32,height:32,borderRadius:8,background:'linear-gradient(135deg,#6366F1,#8B5CF6)',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:13,fontWeight:800,flexShrink:0}}>
              {(activeWorkspace?.name || 'A')[0].toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:10,color:'#94a3b8',margin:0,textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:600}}>Marca activa</p>
              <p style={{fontSize:13,fontWeight:700,color:'#1e293b',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{activeWorkspace?.name}</p>
            </div>
          </div>
        )}
        {/* Navigation */}
        <nav className="flex-1 space-y-2 overflow-y-auto">

                    {/* Marketing Studio - acceso directo */}
          {esAdmin && !_vistaCliente && (
          <button
            onClick={() => navigate('/studio')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              isStudio ? theme.sidebarActive : `${theme.textSecondary} ${theme.sidebarItem}`
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span className="font-medium whitespace-nowrap">Marketing Studio</span>
          </button>
          )}

          {/* Si estamos en Marketing Studio, mostrar secciones */}
          {isStudio && (
            <div className="ml-4 mt-1 space-y-1">
              {sections.map(s => (
                <button
                  key={s.id}
                  onClick={() => onStudioSectionChange?.(s.id)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeStudioSection === s.id ? theme.sidebarActive : `${theme.textSecondary} ${theme.sidebarItem}`
                  }`}
                >
                  <span>· {s.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Resto igual que antes */}
          {navItemsFinal.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive ? theme.sidebarActive : `${theme.textSecondary} ${theme.sidebarItem}`
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
        {/* User Profile - identico al original */}
        <div className={`border-t ${theme.border} pt-6 mt-6`}>
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-meta to-brand-google flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${theme.text} truncate`}>{user?.name}</p>
              <p className={`text-xs ${theme.textSecondary} truncate`}>{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className={`w-full ${theme.border} ${theme.textSecondary} ${theme.cardHover}`}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto ${theme.bg} flex flex-col`}>
        {/* Top bar with global date range */}
        <div className={`sticky top-0 z-30 flex items-center justify-end gap-3 px-8 py-3 border-b ${theme.border} ${theme.bg} backdrop-blur-sm`}>
          {!hideDatePicker && <span className={`text-xs ${theme.textSecondary} hidden md:inline`}>Período:</span>}
          {!hideDatePicker && <DateRangePicker value={range} onChange={setRange} />}
        </div>
        <div className="flex-1">
          {children}
        </div>
      </main>

      <SupportChat />
    </div>
  );
}
